import React, { memo, useMemo } from 'react';
import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import { Event, LocationOn } from '@mui/icons-material';
import { TeamLogo } from './TeamLogo';
import { ScheduleMatch } from '../services/scheduleService';

interface MatchCardProps {
  match: ScheduleMatch;
  highlightTeam?: string; // Keep for API compatibility but use for winning team logic
}

export const MatchCard: React.FC<MatchCardProps> = memo(({
  match
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Responsive logo size
  const logoSize = isDesktop ? 32 : 24;

  // Memoize expensive date formatting
  const { formattedDate, isVenueTime } = useMemo(() => {
    if (!match.dateTime) {
      return { formattedDate: 'TBD', isVenueTime: false };
    }

    try {
      const date = new Date(match.dateTime);
      const isUnverified = match.timezoneVerified === false;
      const tzOption = isUnverified ? { timeZone: 'UTC' as const } : {};
      const dateStr = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...tzOption
      });

      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...tzOption
      });
      return { formattedDate: `${dateStr} • ${timeStr}`, isVenueTime: isUnverified };
    } catch (error) {
      return { formattedDate: 'TBD', isVenueTime: false };
    }
  }, [match.dateTime, match.timezoneVerified]);

  // Determine winning team for highlighting
  const getWinningTeam = () => {
    if (match.status !== 'COMPLETED' || match.homeScore === undefined || match.awayScore === undefined) {
      return null;
    }
    if (match.homeScore > match.awayScore) return match.homeTeam;
    if (match.awayScore > match.homeScore) return match.awayTeam;
    return null; // Draw
  };

  const winningTeam = getWinningTeam();
  const isHomeLosing = match.status === 'COMPLETED' && match.homeScore !== undefined && match.awayScore !== undefined && match.homeScore < match.awayScore;
  const isAwayLosing = match.status === 'COMPLETED' && match.homeScore !== undefined && match.awayScore !== undefined && match.awayScore < match.homeScore;

  // Past match that hasn't been scored yet (not cancelled/postponed)
  const isPlayedUnscored = match.status !== 'COMPLETED' && match.status !== 'CANCELLED' && match.status !== 'POSTPONED'
    && match.dateTime && new Date(match.dateTime) < new Date()
    && (match.homeScore === undefined || match.homeScore === null);

  // Memoize team highlighting logic - highlight winner instead of selected team
  const isHomeHighlighted = useMemo(() => 
    winningTeam === match.homeTeam, [winningTeam, match.homeTeam]
  );
  
  const isAwayHighlighted = useMemo(() => 
    winningTeam === match.awayTeam, [winningTeam, match.awayTeam]
  );

  if (isMobile) {
    // Mobile layout - vertical stack
    return (
      <Box sx={{ py: 2 }}>
        {/* Date and Division First */}
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Event sx={{ fontSize: 14 }} color="action" />
            <Typography variant="caption" color="text.secondary">
              {formattedDate}{isVenueTime && ' (Venue Time)'}
            </Typography>
          </Box>
        </Box>

        {/* Teams and Score */}
        <Box sx={{ mb: 1 }}>
          {/* Home Team */}
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1, opacity: isHomeLosing ? 0.5 : 1 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, pr: 2 }}>
              <TeamLogo 
                teamName={match.homeTeam} 
                logoPath={match.homeTeamLogo?.logoPath} 
                logoUrl={match.homeTeamLogo?.logoUrl}
                size={logoSize} 
              />
              <Typography 
                variant="body2"
                fontWeight={isHomeHighlighted ? 600 : 400}
              >
                {match.homeTeam.length > 60 ? match.homeTeam.substring(0, 60) + '...' : match.homeTeam}
              </Typography>
            </Box>
            {(match.status === 'COMPLETED' || isPlayedUnscored) && (
              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0, color: isPlayedUnscored ? 'text.secondary' : undefined }}>
                {isPlayedUnscored ? '-' : match.homeScore}
              </Typography>
            )}
          </Box>

          {/* Away Team */}
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ opacity: isAwayLosing ? 0.5 : 1 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, pr: 2 }}>
              <TeamLogo
                teamName={match.awayTeam}
                logoPath={match.awayTeamLogo?.logoPath}
                logoUrl={match.awayTeamLogo?.logoUrl}
                size={logoSize}
              />
              <Typography
                variant="body2"
                fontWeight={isAwayHighlighted ? 600 : 400}
              >
                {match.awayTeam.length > 60 ? match.awayTeam.substring(0, 60) + '...' : match.awayTeam}
              </Typography>
            </Box>
            {(match.status === 'COMPLETED' || isPlayedUnscored) && (
              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0, color: isPlayedUnscored ? 'text.secondary' : undefined }}>
                {isPlayedUnscored ? '-' : match.awayScore}
              </Typography>
            )}
          </Box>

        </Box>

        {/* Location and Unverified badge */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={0.5}>
            <LocationOn sx={{ fontSize: 14 }} color="action" />
            <Typography variant="caption" color="text.secondary">
              {match.location || 'TBD'}
            </Typography>
          </Box>
          {match.scoreVerified === false && (
            <Chip label="Unverified" size="small" variant="outlined" color="warning" sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} />
          )}
        </Box>
      </Box>
    );
  }

  // Desktop layout - horizontal
  return (
    <Box sx={{ py: 2, minHeight: 88 }}>
      {/* Date, Division, and Location - centered */}
      <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1} sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
          <Event sx={{ fontSize: 14 }} color="action" />
          <Typography variant="caption" color="text.secondary" noWrap>
            {formattedDate}{isVenueTime && ' (Venue Time)'}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">@</Typography>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
          <LocationOn sx={{ fontSize: 14, flexShrink: 0 }} color="action" />
          <Typography variant="caption" color="text.secondary" noWrap>
            {match.location || 'TBD'}
          </Typography>
        </Box>
      </Box>
      
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        {/* Home Team */}
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1, opacity: isHomeLosing ? 0.5 : 1, justifyContent: 'flex-end' }}>
          <Typography 
            variant="body2"
            fontWeight={isHomeHighlighted ? 600 : 400}
            textAlign="right"
          >
            {match.homeTeam.length > 60 ? match.homeTeam.substring(0, 60) + '...' : match.homeTeam}
          </Typography>
        </Box>
        
        {/* Score or VS with logos */}
        <Box sx={{ mx: 2, minWidth: 'fit-content', display: 'flex', alignItems: 'center', gap: 1 }}>
          {(match.status === 'COMPLETED' || isPlayedUnscored) ? (
            <>
              <Box sx={{ opacity: isHomeLosing ? 0.5 : 1 }}>
                <TeamLogo
                  teamName={match.homeTeam}
                  logoPath={match.homeTeamLogo?.logoPath}
                  logoUrl={match.homeTeamLogo?.logoUrl}
                  size={logoSize}
                />
              </Box>
              <Typography variant="h6" textAlign="center" sx={{ lineHeight: 1, color: isPlayedUnscored ? 'text.secondary' : undefined }}>
                {isPlayedUnscored ? (
                  '- - -'
                ) : (
                  <>
                    <Box component="span" sx={{ fontWeight: isHomeHighlighted ? 700 : 400, opacity: isHomeLosing ? 0.5 : 1 }}>
                      {match.homeScore}
                    </Box>
                    {' - '}
                    <Box component="span" sx={{ fontWeight: isAwayHighlighted ? 700 : 400, opacity: isAwayLosing ? 0.5 : 1 }}>
                      {match.awayScore}
                    </Box>
                  </>
                )}
              </Typography>
              <Box sx={{ opacity: isAwayLosing ? 0.5 : 1 }}>
                <TeamLogo
                  teamName={match.awayTeam}
                  logoPath={match.awayTeamLogo?.logoPath}
                  logoUrl={match.awayTeamLogo?.logoUrl}
                  size={logoSize}
                />
              </Box>
            </>
          ) : (
            <>
              <TeamLogo
                teamName={match.homeTeam}
                logoPath={match.homeTeamLogo?.logoPath}
                logoUrl={match.homeTeamLogo?.logoUrl}
                size={logoSize}
              />
              <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ lineHeight: 1 }}>
                VS
              </Typography>
              <TeamLogo
                teamName={match.awayTeam}
                logoPath={match.awayTeamLogo?.logoPath}
                logoUrl={match.awayTeamLogo?.logoUrl}
                size={logoSize}
              />
            </>
          )}
        </Box>
        
        {/* Away Team */}
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1, justifyContent: 'flex-start', opacity: isAwayLosing ? 0.5 : 1 }}>
          <Typography
            variant="body2"
            fontWeight={isAwayHighlighted ? 600 : 400}
            textAlign="left"
          >
            {match.awayTeam.length > 60 ? match.awayTeam.substring(0, 60) + '...' : match.awayTeam}
          </Typography>
        </Box>
      </Box>

      {/* Unverified indicator */}
      {match.scoreVerified === false && (
        <Box display="flex" justifyContent="center">
          <Chip label="Unverified" size="small" variant="outlined" color="warning" sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} />
        </Box>
      )}
    </Box>
  );
});
